/**
 * What an object turns out to be, worked out from what hangs off it.
 *
 * Apart from the panel that draws it, because it is a question about the
 * content rather than a piece of interface, and because a file that exports
 * both cannot be swapped in while the game is running.
 */

import type { ObjectDefinitionWire, RecipeWire } from '@inverse/protocol';
import type { Condition, Effect } from './Dialogue';

/** What an object turns out to be, worked out from what hangs off it. */
export type ObjectSort = 'harvest' | 'craft' | 'utility' | 'other';

export const SORT_LABELS: Readonly<Record<ObjectSort, string>> = {
  harvest: 'Harvested',
  craft: 'Made at',
  utility: 'Used',
  other: 'Other',
};

/** Everything the world hangs off an object, gathered for one of them. */
export interface ObjectLinks {
  yields: readonly { objectId: number; itemId: number }[];
  recipes: readonly RecipeWire[];
  shops: readonly { id: number; name: string }[];
}

/**
 * Which of the four an object is.
 *
 * Asked of the content rather than of the name: an anvil is a place things are
 * made because recipes point at it, not because it is called an anvil.
 */
export function sortOf(object: ObjectDefinitionWire, links: ObjectLinks): ObjectSort {
  if (links.yields.some((one) => one.objectId === object.id)) return 'harvest';
  if (links.recipes.some((one) => one.objectId === object.id)) return 'craft';

  const opens = object.actions.some(
    (action) => action === 'Open' || action === 'Deposit' || action === 'Trade',
  );
  if (opens || object.dungeon || object.exit) return 'utility';

  return 'other';
}

/**
 * One thing a piece of the world does when it is pressed.
 *
 * The same shape the server holds, and the same two lists a line of
 * conversation carries. Read off the definitions, which carry it as written
 * text rather than as a wire format of its own.
 */
/** What sets an act off: a menu entry, or something that happens to it. */
export type ActOn = 'menu' | 'hurt' | 'dies' | 'sees';

export const ACT_ON_LABELS: Readonly<Record<ActOn, string>> = {
  menu: 'A menu entry',
  hurt: 'When it is hurt',
  dies: 'When it dies',
  sees: 'When it sees somebody',
};

export interface Act {
  on: ActOn;
  /** For one that fires when it is hurt: the share of its health, out of a hundred. */
  atHealth: number;
  action: string;
  when: Condition[];
  does: Effect[];
  refuses: string;
  hidden: boolean;
  /** How many times it may ever run, and for whom. */
  once: 'always' | 'player' | 'world';
  /** Said to somebody who finds it already done. */
  doneSays: string;
}

/**
 * The acts written on an object.
 *
 * Anything that cannot be read comes back as nothing rather than throwing: a
 * definition somebody has edited by hand should cost that object its behaviour
 * in the panel, not cost the panel its ability to open.
 */
const isOn = (held: unknown): held is ActOn =>
  held === 'menu' || held === 'hurt' || held === 'dies' || held === 'sees';

export function actsOf(object: { acts: string }): Act[] {
  if (!object.acts) return [];

  try {
    const held: unknown = JSON.parse(object.acts);
    if (!Array.isArray(held)) return [];

    return held
      .filter((one): one is Partial<Act> => typeof one === 'object' && one !== null)
      .map((one) => ({
        // A menu entry unless it says otherwise, so everything written before
        // occasions existed goes on meaning what it meant.
        on: isOn(one.on) ? one.on : 'menu',
        atHealth: typeof one.atHealth === 'number' ? one.atHealth : 50,
        action: typeof one.action === 'string' ? one.action : '',
        when: Array.isArray(one.when) ? one.when : [],
        does: Array.isArray(one.does) ? one.does : [],
        refuses: typeof one.refuses === 'string' ? one.refuses : '',
        hidden: one.hidden === true,
        once: one.once === 'player' || one.once === 'world' ? one.once : 'always',
        doneSays: typeof one.doneSays === 'string' ? one.doneSays : '',
      }));
  } catch {
    return [];
  }
}
